import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Shell } from "@/components/Shell";
import { SignIn } from "@/features/session/SignIn";
import { LokaleAccounts } from "@/features/lokale-accounts/LokaleAccounts";
import { useMe } from "@/features/session/useMe";
import { keepScrollPosition } from "@jbnl/shared-ui";

export function LokaleAccountsPage() {
  const { t } = useTranslation(["paginas", "navigatie", "common"]);
  const { data: me, isPending } = useMe();
  const {
    account: accountId,
    filter,
    deur,
    status,
    pagina,
  } = useSearch({ from: "/lokale-accounts" });
  const navigate = useNavigate({ from: "/lokale-accounts" });

  if (isPending) {
    return (
      <Shell>
        <PageSkeleton />
      </Shell>
    );
  }

  if (!me) {
    return (
      <Shell>
        <SignIn />
      </Shell>
    );
  }

  // The API refuses this to anyone else regardless; hiding it here only avoids showing a
  // korpscontact a screen that would answer 403 to everything on it.
  if (!me.isBeheerder) {
    return (
      <Shell name={me.name} letterhead>
        <Card>
          <CardHeader>
            <CardTitle>{t("common:doors.noAccess")}</CardTitle>
            <CardDescription>
              {t("common:doors.noAccessDescription")}
            </CardDescription>
          </CardHeader>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell name={me.name} wide>
      {/* The title above both panes rather than inside one, as on seizoenen and
          wedstrijddagen: a card wrapped around the pair cannot contain a list that has to
          scroll inside its own frame. */}
      <header className="mb-6 space-y-1">
        <h1 className="text-2xl font-bold tracking-[-0.02em]">
          {t("navigatie:paginas.lokaleAccounts.label")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("lokaleAccounts.uitleg")}
        </p>
      </header>

      <LokaleAccounts
        accountId={accountId}
        onKies={(id) =>
          void navigate({
            ...keepScrollPosition,
            search: (previous) => ({ ...previous, account: id }),
          })
        }
        filter={filter ?? "alle"}
        // Back to page 1 with the filter: page 3 of every account is past the end of the three
        // that are waiting, and the chosen account drops out of a slice it is not in anyway.
        onFilterChange={(next) =>
          void navigate({
            ...keepScrollPosition,
            search: (previous) => ({
              ...previous,
              filter: next === "alle" ? undefined : next,
              pagina: undefined,
              account: undefined,
            }),
          })
        }
        deur={deur}
        status={status}
        // Same reason as `onFilterChange`: a filter narrows what the server counts, so page 3 of
        // the old set can land past the end of the new, shorter one.
        onFiltersChange={(volgende) =>
          void navigate({
            ...keepScrollPosition,
            search: (previous) => ({
              ...previous,
              deur: volgende.deur,
              status: volgende.status,
              pagina: undefined,
              account: undefined,
            }),
          })
        }
        pagina={pagina}
        onPaginaChange={(next) =>
          void navigate({
            search: (previous) => ({ ...previous, pagina: next }),
            ...keepScrollPosition,
          })
        }
      />
    </Shell>
  );
}
